import type { LineTax, RenditionInvoice, RenditionLine, RenditionLinePart, RenditionSharePerson } from '@/domain/rendition/Rendition'
import type { UserRepository } from '@/domain/user/UserRepository'
import type { User } from '@/domain/user/User'
import type { DiscountType } from '@/types/invoice'
import { getLineNet } from '@/utils/invoiceLineNet'
import { isIvaTax, projectTaxes, type TaxLike } from '@/utils/taxes'
import { resolveWithholdingAmount } from '@/utils/withholdings'

// The part is claimed line by line, VAT included, and capped at what each line charges, as the api's
// `InvoiceShareServices.line_values`; `test_invoice_share_line_values.py` pins the same numbers.

/** How the payload rounds every amount it sends. */
export function roundTo(v: number, decimals: number): number {
  const factor = 10 ** decimals
  return Math.round(v * factor) / factor
}

/** Round half to even, like the backend's `ROUND_HALF_EVEN` and Python's `round()`: 2502.5 → 2502, 2503.5 → 2504. */
export function roundHalfEven(value: number, decimals: number): number {
  const factor = 10 ** decimals
  const scaled = value * factor
  const floor = Math.floor(scaled)
  const tolerance = Math.max(1e-9, Math.abs(scaled) * 1e-14)
  if (Math.abs(scaled - floor - 0.5) > tolerance) return Math.round(scaled) / factor
  return (floor % 2 === 0 ? floor : floor + 1) / factor
}

function toTaxLike(tax: LineTax, decimals: number): TaxLike {
  return {
    tax_name: tax.taxName,
    tax_type: tax.taxName,
    percentage: tax.taxPercentage,
    taxable_amount: roundTo(tax.taxableAmount || 0, decimals),
    tax_amount: roundTo(tax.taxAmount || 0, decimals),
  }
}

/**
 * The pieces of a line's charge, as `InvoiceLineService.charged_total` reads them. Never `line.total`: a receipt
 * from Mis Archivos brings its gross there, and adding its taxes again would count the VAT twice.
 */
function lineAmounts(
  line: RenditionLine,
  decimals: number,
  country?: string | null,
  discountType?: DiscountType,
): { net: number; taxes: number; withheld: number } {
  const net = getLineNet({ unit_price: roundTo(line.unitPrice || 0, decimals), quantity: line.quantity, discount: line.discount || 0 }, discountType)
  const projected = projectTaxes(
    line.taxes.filter((t) => t.taxTypeCode === 'TAX').map((t) => toTaxLike(t, decimals)),
    line.taxes.filter((t) => t.taxTypeCode === 'WITHHOLDING').map((t) => toTaxLike(t, decimals)),
    country,
  )
  const taxes = projected.taxes.reduce((sum, t) => sum + (t.tax_amount || 0), 0)
  const iva = projected.taxes.filter((t) => isIvaTax(t, country)).reduce((sum, t) => sum + (t.tax_amount || 0), 0)
  const withheld = projected.withholdings.reduce((sum, w) => sum + resolveWithholdingAmount(w, net, iva, country), 0)
  return { net, taxes, withheld }
}

/** What one line charges on its own: its net plus its taxes minus its withholdings. */
export function shareLineCharge(line: RenditionLine, decimals: number, country?: string | null, discountType?: DiscountType): number {
  const { net, taxes, withheld } = lineAmounts(line, decimals, country, discountType)
  return net + taxes - withheld
}

/**
 * What the receipt charges for its lines, tip out, the way the backend recomputes its header from them: the net
 * total, the taxes and the withholdings each rounded to the currency before they are added up, less the header
 * discount of an invoice that already has one.
 */
export function shareDocumentCharge(inv: RenditionInvoice, decimals: number, country?: string | null): number {
  let net = 0
  let taxes = 0
  let withheld = 0
  for (const line of inv.lines) {
    const amounts = lineAmounts(line, decimals, country, inv.discountType)
    net += amounts.net
    taxes += amounts.taxes
    withheld += amounts.withheld
  }
  const discount = roundTo(inv.discountTotal || 0, decimals)
  const charged = roundHalfEven(net, decimals) + roundHalfEven(taxes, decimals) - roundHalfEven(withheld, decimals) - discount
  return Math.max(roundHalfEven(charged, decimals), 0)
}

/**
 * Pool receipt whose edited lines drift more than one unit from every amount the api accepts (as they arrived, the
 * printed total, or it without the tip): the save would rebuild them (`invoice_share_lines_changed`).
 */
export function sharePrintedTotalMismatch(
  inv: RenditionInvoice,
  decimals: number,
  country?: string | null,
): { lines: number; printed: number } | null {
  if (inv.shareOwnedByOther || !inv.receivedLines?.length) return null
  const lines = shareDocumentCharge(inv, decimals, country)
  const targets = [shareDocumentCharge({ ...inv, lines: inv.receivedLines }, decimals, country)]
  if (inv.nativeTotal != null) targets.push(inv.nativeTotal, inv.nativeTotal - (inv.tip || 0))
  const closest = targets.reduce((best, target) => (Math.abs(lines - target) < Math.abs(lines - best) ? target : best))
  return Math.abs(lines - closest) > 1 ? { lines, printed: roundTo(closest, decimals) } : null
}

/**
 * What each line charges: the receipt's charge without its tip, spread over its positive lines by their own charge.
 * Values round half to even and the largest line takes the residue, so they add up to exactly the receipt's charge.
 */
export function shareLineValues(inv: RenditionInvoice, decimals: number, country?: string | null): number[] {
  const weights = inv.lines.map((line) => {
    const { net, taxes, withheld } = lineAmounts(line, decimals, country, inv.discountType)
    const charge = net + taxes - withheld
    return net > 0 && charge > 0 ? charge : 0
  })
  const totalWeight = weights.reduce((sum, w) => sum + w, 0)
  if (totalWeight <= 0) return inv.lines.map(() => 0)
  const charged = shareDocumentCharge(inv, decimals, country)
  const values = weights.map((w) => (w > 0 ? roundHalfEven((charged * w) / totalWeight, decimals) : 0))
  const largest = weights.indexOf(Math.max(...weights))
  values[largest] = roundHalfEven(values[largest] + charged - values.reduce((sum, v) => sum + v, 0), decimals)
  return values
}

export interface SpreadLinePart extends RenditionLinePart {
  percent: number
}

/**
 * How a corrected line spreads over its parts, as the api's `InvoiceShareServices.reapply_parts` does on save: an
 * approved part keeps its amount; any other takes its share of the line's value and, when that no longer fits next
 * to the approved ones, they shrink in proportion. Amounts round half to even; the residue stays free, unless the
 * parts take the whole line: then it goes to the owner's part, or to the largest one when hers is not on it.
 */
export function spreadLineParts(
  parts: RenditionLinePart[],
  value: number,
  decimals: number,
): { parts: SpreadLinePart[]; free: number; approved: number; belowApproved: boolean } {
  const approved = roundHalfEven(parts.filter((p) => p.approved).reduce((sum, p) => sum + p.amount, 0), decimals)
  const room = Math.max(value - approved, 0)
  const moving = parts.filter((p) => !p.approved)
  const wanted = moving.map((p) => value * p.fraction)
  const total = wanted.reduce((sum, w) => sum + w, 0)
  const scale = total > 0 ? Math.min(1, room / total) : 0
  const amounts = wanted.map((w) => roundHalfEven(w * scale, decimals))
  const limit = roundHalfEven(room, decimals)
  const sum = roundHalfEven(amounts.reduce((acc, a) => acc + a, 0), decimals)
  const residue = roundHalfEven((roundHalfEven(total, decimals) >= limit ? limit : Math.min(limit, sum)) - sum, decimals)
  if (residue && amounts.length) {
    const owner = moving.findIndex((p) => p.owner)
    const absorber = owner >= 0 ? owner : amounts.indexOf(Math.max(...amounts))
    amounts[absorber] = roundHalfEven(amounts[absorber] + residue, decimals)
  }
  const spread = parts.map((p) => {
    const amount = p.approved ? p.amount : amounts[moving.indexOf(p)]
    return { ...p, amount, percent: value > 0 ? (amount / value) * 100 : 0 }
  })
  const taken = spread.reduce((acc, p) => acc + p.amount, 0)
  return { parts: spread, free: Math.max(roundHalfEven(value - taken, decimals), 0), approved, belowApproved: approved > value }
}

/**
 * Who else has a part of a shared receipt whose currency changed since it was loaded: the api moves each of
 * those parts to the rate of its own rendition on save (`reapply_parts`), which the save confirms first.
 */
export function sharedCurrencyChangePeople(inv: RenditionInvoice): string[] {
  if (!inv.shares?.length || !inv.shareCurrencyWhenLoaded || inv.currency === inv.shareCurrencyWhenLoaded) return []
  const names = new Set<string>()
  inv.lines.forEach((line) => line.shareParts?.forEach((part) => { if (!part.mine) names.add(part.name) }))
  return [...names]
}

/**
 * The requester's tip: what they typed, or the receipt's tip in the proportion their lines take of what it
 * charges, never more than what the other parts left of it.
 */
export function shareTipOf(inv: RenditionInvoice, decimals: number, country?: string | null): number {
  if (inv.shareTip !== undefined) return inv.shareTip
  const tip = inv.tip || 0
  const charged = shareDocumentCharge(inv, decimals, country)
  if (!tip || charged <= 0) return 0
  const mine = inv.lines.reduce((sum, line) => sum + (line.shareTotal ?? 0), 0)
  return Math.min(roundHalfEven(tip * Math.min(mine / charged, 1), decimals), inv.shareTipAvailable ?? tip)
}

/** The part the preview prices instead of the whole document; undefined on a receipt that is not shared. */
export function sharePreviewPart(
  inv: RenditionInvoice,
  decimals: number,
  country?: string | null,
): { lines: { amount: number }[]; tipAmount: number } | undefined {
  if (!inv.shares?.length) return undefined
  return {
    lines: inv.lines.filter((line) => (line.shareTotal ?? 0) > 0).map((line) => ({ amount: roundTo(line.shareTotal as number, decimals) })),
    tipAmount: shareTipOf(inv, decimals, country),
  }
}

function toPerson(user: User): RenditionSharePerson {
  return { id: user.id as number, name: user.name, image: user.profile_picture || null }
}

/**
 * The requester and the people they can share a receipt with: active users of their own society only,
 * because the backend rejects designating someone from a sibling society of a consolidated company.
 */
export async function loadSharedExpensePeople(repo: UserRepository): Promise<{ me: RenditionSharePerson; candidates: RenditionSharePerson[] }> {
  const [self, users] = await Promise.all([repo.getSelf(), repo.listSocietyUsers({ ownSociety: true })])
  return {
    me: toPerson(self),
    candidates: users.filter((user) => user.id != null && user.id !== self.id).map(toPerson),
  }
}
